import { useEffect, useId, useMemo, useState } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import styles from "./services.module.css";
import { ButtonBack, ButtonSave } from "../../components/ui";
import { usePlanFeature } from "../../hooks/usePlanFeature";

/**
 * Duração enviada para itens de CATÁLOGO (tenant sem agendamento). O backend
 * exige `duration Int` e o campo NÃO será afrouxado — seria destrutivo num
 * campo que o agendamento usa (ADR-0026 §4). A assimetria fica no frontend: o
 * campo some da tela e vai um default. 30 é o mesmo default do
 * `TenantSetupModal`, para não haver dois números canônicos.
 */
const CATALOG_DEFAULT_DURATION = 30;

function formatBRL(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function minutesLabel(value) {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return "";
  return `${n} min`;
}

export default function ServicesPage() {
  // 🔴 Esta tela NUNCA é bloqueada: é a fonte do `query_price` e o catálogo de
  // preços do Plano Atendimento (ADR-0026 §4). O plano só muda o ENQUADRAMENTO:
  // sem agendamento, "Serviços" vira "Serviços e preços" e a duração some.
  // Fail-safe: `allowed` só é false com `schedulingIncluded === false`, então
  // quem tem agenda (ou ainda não hidratou o plano) vê a tela idêntica à de
  // hoje.
  const { allowed: schedulingIncluded } = usePlanFeature("scheduling");
  const isCatalogOnly = !schedulingIncluded;

  const [services, setServices] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [complementaryServiceIds, setComplementaryServiceIds] = useState([]);

  // Validação exibida só após interação: campos pristinos ficam neutros.
  // `touched` marca no blur; `submitAttempted` libera todos os erros ao enviar.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const uid = useId();

  const fetchServices = async () => {
    setStatus("loading");
    try {
      const response = await api.get("/services");
      setServices(response.data || []);
      setStatus("ready");
    } catch (error) {
      setStatus("error");
      toast.error("Erro ao carregar serviços.");
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const formErrors = useMemo(() => {
    const errors = {};

    if (!newName.trim() || newName.trim().length < 3) {
      errors.name = "Informe um nome com pelo menos 3 caracteres.";
    }

    const price = Number(newPrice);
    if (Number.isNaN(price) || price <= 0) {
      errors.price = "Informe um preço maior que 0.";
    }

    // Sem agendamento o campo não existe na tela — validar seria travar o
    // formulário num erro que o usuário não tem como corrigir.
    if (!isCatalogOnly) {
      const duration = Number(newDuration);
      if (!Number.isInteger(duration) || duration <= 0) {
        errors.duration = "Informe uma duração (min) maior que 0.";
      }
    }

    return errors;
  }, [newName, newPrice, newDuration, isCatalogOnly]);

  // Só mostra o erro de um campo depois que o usuário saiu dele (blur) ou tentou
  // enviar — evita vermelho no estado pristino.
  const showError = (field) =>
    Boolean((touched[field] || submitAttempted) && formErrors[field]);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const canSubmit =
    status === "ready" && !saving && Object.keys(formErrors).length === 0;

  const handleDelete = async (service) => {
    const ok = window.confirm(
      `Tem certeza que deseja remover "${service.name}"?`,
    );
    if (!ok) return;

    try {
      await api.delete(`/services/${service.id}`);
      setServices((prev) => prev.filter((s) => s.id !== service.id));
      toast.info("Serviço removido.");
    } catch (error) {
      toast.error("Erro ao deletar.");
    }
  };

  const handleEditClick = (service) => {
    setEditingId(service.id);
    setNewName(service.name);
    setNewPrice(service.price.toString());
    setNewDuration(service.duration.toString());
    setComplementaryServiceIds(service.complementaryServiceIds ?? []);
    setTouched({});
    setSubmitAttempted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNewName("");
    setNewPrice("");
    setNewDuration("");
    setComplementaryServiceIds([]);
    setTouched({});
    setSubmitAttempted(false);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!canSubmit) return;

    setSaving(true);
    try {
      // No modo catálogo a duração vem do default (campo escondido). Na edição,
      // `newDuration` já foi preenchido com o valor gravado em
      // `handleEditClick` — então editar um item de catálogo PRESERVA a duração
      // existente em vez de sobrescrevê-la com 30.
      const duration = isCatalogOnly
        ? Number(newDuration) || CATALOG_DEFAULT_DURATION
        : Number(newDuration);

      const payload = {
        name: newName.trim(),
        price: Number(newPrice),
        duration,
        ...(editingId ? { complementaryServiceIds } : {}),
      };

      if (editingId) {
        //MODO EDIÇÃO (PATCH)
        const response = await api.patch(`/services/${editingId}`, payload);
        setServices((prev) =>
          prev.map((s) => (s.id === editingId ? response.data : s)),
        );
        toast.success("Serviço atualizado!");
      } else {
        //MODO ADIÇÃO (POST)
        const response = await api.post("/services", payload);
        setServices((prev) => [...prev, response.data]);
        toast.success("Serviço adicionado!");
      }

      handleCancelEdit();
    } catch (error) {
      toast.error(editingId ? "Erro ao atualizar." : "Erro ao adicionar.");
    } finally {
      setSaving(false);
    }
  };

  const toggleComplementary = (id) => {
    setComplementaryServiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div
      className={styles.page}
      aria-busy={status === "loading" ? "true" : "false"}
    >
      <div className={styles.container}>
        {/* Sem agendamento, /agenda cai na tela de upsell — voltar para lá
            seria um beco. Manda para a visão geral. */}
        <ButtonBack to={isCatalogOnly ? "/visao-geral" : "/agenda"} />

        <header className={styles.header}>
          <h1 className={styles.title}>
            {isCatalogOnly ? "Serviços e preços" : "Gestão de Serviços"}
          </h1>
          <p className={styles.subtitle} id={`${uid}-subtitle`}>
            {isCatalogOnly
              ? "Cadastre o que você faz e quanto custa. É desta lista que o assistente responde “quanto custa?” no WhatsApp."
              : "Cadastre, revise e remova serviços disponíveis para agendamento."}
          </p>
        </header>

        {status === "loading" && (
          <section className={styles.card} aria-label="Carregando">
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonRow} />
            <div className={styles.skeletonRow} />
            <div className={styles.skeletonRow} />
            <p className={styles.muted}>Carregando serviços…</p>
          </section>
        )}

        {status === "error" && (
          <section
            className={styles.card}
            aria-label="Erro ao carregar serviços"
          >
            <p className={styles.errorText}>
              Não foi possível carregar seus serviços agora.
            </p>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={fetchServices}
            >
              Tentar novamente
            </button>
          </section>
        )}

        {status === "ready" && (
          <>
            <section
              className={styles.card}
              aria-describedby={`${uid}-subtitle`}
            >
              <form onSubmit={handleSaveService} className={styles.form}>
                <fieldset className={styles.fieldset}>
                  <legend className={styles.legend}>
                    {editingId
                      ? isCatalogOnly
                        ? "Editar item"
                        : "Editar serviço"
                      : isCatalogOnly
                        ? "Cadastrar novo item"
                        : "Cadastrar novo serviço"}
                  </legend>

                  <div className={styles.grid}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor={`${uid}-name`}>
                        Nome do serviço
                      </label>
                      <input
                        id={`${uid}-name`}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onBlur={() => markTouched("name")}
                        className={`${styles.input} ${showError("name") ? styles.invalid : ""}`}
                        placeholder="Ex.: Corte masculino"
                        autoComplete="off"
                        aria-invalid={showError("name") ? "true" : "false"}
                        aria-describedby={
                          showError("name") ? `${uid}-name-error` : undefined
                        }
                      />
                      {showError("name") && (
                        <p
                          className={styles.fieldError}
                          id={`${uid}-name-error`}
                          role="alert"
                        >
                          {formErrors.name}
                        </p>
                      )}
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label} htmlFor={`${uid}-price`}>
                        Preço (R$)
                      </label>
                      <input
                        id={`${uid}-price`}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        onBlur={() => markTouched("price")}
                        className={`${styles.input} ${showError("price") ? styles.invalid : ""}`}
                        placeholder="Ex.: 50"
                        aria-invalid={showError("price") ? "true" : "false"}
                        aria-describedby={
                          showError("price")
                            ? `${uid}-price-error`
                            : `${uid}-price-hint`
                        }
                      />
                      {showError("price") && (
                        <p
                          className={styles.fieldError}
                          id={`${uid}-price-error`}
                          role="alert"
                        >
                          {formErrors.price}
                        </p>
                      )}
                    </div>

                    {/* Duração só faz sentido com agenda. Sem ela, o campo NÃO
                        é renderizado (`hidden` não bastaria: `.field` é
                        `display:flex`, que vence o atributo) e o payload leva o
                        default — o dado no banco continua íntegro (ADR-0026 §4). */}
                    {!isCatalogOnly && (
                    <div className={styles.field}>
                      <label
                        className={styles.label}
                        htmlFor={`${uid}-duration`}
                      >
                        Duração (minutos)
                      </label>
                      <input
                        id={`${uid}-duration`}
                        type="number"
                        inputMode="numeric"
                        step="1"
                        min="1"
                        value={newDuration}
                        onChange={(e) => setNewDuration(e.target.value)}
                        onBlur={() => markTouched("duration")}
                        className={`${styles.input} ${showError("duration") ? styles.invalid : ""}`}
                        placeholder="Ex.: 30"
                        aria-invalid={showError("duration") ? "true" : "false"}
                        aria-describedby={
                          showError("duration")
                            ? `${uid}-duration-error`
                            : `${uid}-duration-hint`
                        }
                      />
                      {showError("duration") && (
                        <p
                          className={styles.fieldError}
                          id={`${uid}-duration-error`}
                          role="alert"
                        >
                          {formErrors.duration}
                        </p>
                      )}
                    </div>
                    )}

                    {editingId && (
                      <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
                        <label className={styles.label}>Serviços complementares (upsell)</label>
                        <p className={styles.hint}>
                          Selecione serviços que podem ser oferecidos como add-on ao confirmar o agendamento.
                        </p>
                        <div className={styles.checkboxGroup}>
                          {services
                            .filter((s) => s.id !== editingId)
                            .map((s) => (
                              <label key={s.id} className={styles.checkboxLabel}>
                                <input
                                  type="checkbox"
                                  checked={complementaryServiceIds.includes(s.id)}
                                  onChange={() => toggleComplementary(s.id)}
                                />
                                <span>{s.name} — {formatBRL(s.price)}</span>
                              </label>
                            ))}
                          {services.filter((s) => s.id !== editingId).length === 0 && (
                            <p className={styles.muted}>Nenhum outro serviço disponível.</p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={styles.actions}>
                      <ButtonSave
                        type="submit"
                        isLoading={saving}
                        loadingText="Salvando…"
                        disabled={!canSubmit}
                      >
                        {editingId ? "Salvar Alterações" : "Adicionar"}
                      </ButtonSave>

                      {editingId && (
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          onClick={handleCancelEdit}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                </fieldset>
              </form>
            </section>

            <section
              className={styles.listSection}
              aria-label="Lista de serviços"
            >
              <div className={styles.listHeader}>
                <h2 className={styles.sectionTitle}>
                  {isCatalogOnly
                    ? "Itens cadastrados"
                    : "Serviços cadastrados"}
                </h2>
                <span
                  className={styles.badge}
                  aria-label={`Total de serviços: ${services.length}`}
                >
                  {services.length}
                </span>
              </div>

              {services.length === 0 ? (
                <div
                  className={styles.emptyState}
                  role="status"
                  aria-live="polite"
                >
                  <p className={styles.emptyTitle}>
                    {isCatalogOnly
                      ? "Nenhum item cadastrado"
                      : "Nenhum serviço cadastrado"}
                  </p>
                  <p className={styles.muted}>
                    {isCatalogOnly
                      ? "Cadastre seu primeiro item no formulário acima — sem ele o assistente não sabe informar preços."
                      : "Cadastre seu primeiro serviço no formulário acima."}
                  </p>
                </div>
              ) : (
                <ul className={styles.list}>
                  {services.map((service) => (
                    <li key={service.id} className={styles.serviceCard}>
                      <div className={styles.serviceInfo}>
                        <p className={styles.serviceName}>{service.name}</p>
                        <p className={styles.serviceMeta}>
                          {!isCatalogOnly && (
                            <>
                              <span>{minutesLabel(service.duration)}</span>
                              <span className={styles.dot} aria-hidden="true">
                                •
                              </span>
                            </>
                          )}
                          <span>{formatBRL(service.price)}</span>
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleEditClick(service)}
                        className={styles.editButton}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(service)}
                        className={styles.dangerButton}
                        aria-label={`Remover serviço ${service.name}`}
                      >
                        Remover
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
